// src/belediye/redux/slices/belediyeCloseApprovalSlice.js
//
// Close Approval domain state’i.
//
//   current: sadece pending
//   history: pending olmayanlar (lazy-load; cache varsa tekrar fetch etmez)
//   meta: sonuç kartı için "history’den bağımsız" approved meta (lazy history açılınca sonuç değişmesin)
//
// Debug:
// - .env => REACT_APP_DEBUG_CLOSE_APPROVAL=1
// - Konsolda [closeApprovalSlice] prefix'i ile log basar.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  listCloseRequests,
  retrieveCloseRequest,
  createCloseRequest as apiCreateCloseRequest,
  cancelCloseRequest as apiCancelCloseRequest,
  decideCloseRequest as apiDecideCloseRequest,
} from "../../services/closeApprovalApi";

import {
  fetchApplicationDetails,
  fetchApplicationStatusLogs,
} from "./belediyeApplicationDetailsSlice";

// -------------------------------------------------------------
// Debug helper
// -------------------------------------------------------------
const DEBUG =
  String(process.env.REACT_APP_DEBUG_CLOSE_APPROVAL || "").trim() === "1";

const log = (...args) => {
  if (DEBUG) console.log("[closeApprovalSlice]", ...args);
};

const warn = (...args) => {
  if (DEBUG) console.warn("[closeApprovalSlice]", ...args);
};

// -------------------------------------------------------------
// Helpers
// -------------------------------------------------------------
const LS = { accessToken: "accessToken" };

const getAccessTokenSafe = () => {
  try {
    return localStorage.getItem(LS.accessToken);
  } catch {
    return null;
  }
};

const normalizeError = (err, fallback = "İşlem başarısız.") => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data) {
    if (typeof data === "string") return data;
    if (typeof data?.detail === "string") return data.detail;

    if (typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      const v = data[firstKey];
      if (Array.isArray(v) && v[0]) return String(v[0]);
      if (typeof v === "string") return v;
    }
  }

  if (status) return `${fallback} (HTTP ${status})`;
  return err?.message || fallback;
};

const isPending = (cr) => String(cr?.status || "") === "pending";
const isFinal = (cr) => {
  const st = String(cr?.status || "");
  return Boolean(st && st !== "pending");
};
const isApproved = (cr) => String(cr?.status || "") === "approved";

const uniqByIdDesc = (items) => {
  const list = Array.isArray(items) ? items : [];
  const seen = new Set();
  const out = [];

  for (const x of list) {
    const id = Number(x?.id || 0);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(x);
  }

  out.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  return out;
};

const pickPendingIdForApplication = (listItems, applicationId) => {
  const appId = Number(applicationId);
  if (!appId) return null;

  const matched = (Array.isArray(listItems) ? listItems : []).filter(
    (x) => Number(x?.application_id) === appId
  );
  if (!matched.length) return null;

  const pending = matched.find((x) => String(x?.status) === "pending");
  return pending?.id ? Number(pending.id) : null;
};

const pickFinalItemsForApplication = (listItems, applicationId) => {
  const appId = Number(applicationId);
  if (!appId) return [];

  const matched = (Array.isArray(listItems) ? listItems : []).filter(
    (x) => Number(x?.application_id) === appId
  );

  const finals = matched.filter(
    (x) => String(x?.status || "") && String(x.status) !== "pending"
  );
  finals.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  return finals;
};

//   META helper: application için en yeni approved ID (list item içinden)
const pickLatestApprovedIdForApplication = (listItems, applicationId) => {
  const appId = Number(applicationId);
  if (!appId) return null;

  const matched = (Array.isArray(listItems) ? listItems : []).filter(
    (x) => Number(x?.application_id) === appId
  );
  if (!matched.length) return null;

  const approved = matched.filter((x) => String(x?.status) === "approved");
  if (!approved.length) return null;

  // listCloseRequests genelde desc gelir ama defans:
  approved.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  const top = approved[0];
  return top?.id ? Number(top.id) : null;
};

// -------------------------------------------------------------
// Thunks
// -------------------------------------------------------------

/**
 *   "Bu başvuru için bekleyen talep var mı?"
 *
 * - Cache varsa (force değilse) tekrar fetch etmez.
 * - Pending varsa -> retrieve detail -> current = detail
 * - Pending yoksa -> current = null
 *
 * Param:
 *  fetchCloseRequestForApplication({ applicationId, force=false })
 */
export const fetchCloseRequestForApplication = createAsyncThunk(
  "belediyeCloseApproval/fetchCloseRequestForApplication",
  async ({ applicationId, force = false } = {}, { getState, rejectWithValue }) => {
    try {
      const appId = Number(applicationId);
      if (!appId) throw new Error("applicationId zorunlu");

      const state = getState?.();
      const cached =
        state?.belediyeCloseApproval?.byApplicationId?.[String(appId)]?.current;

      if (!force && cached && isPending(cached)) {
        log("fetchCloseRequestForApplication:cache-hit", { appId, id: cached?.id });
        return { applicationId: appId, pending: cached, fromCache: true };
      }

      log("fetchCloseRequestForApplication:start", { appId, force });

      const list = await listCloseRequests({});
      const pendingId = pickPendingIdForApplication(list, appId);

      if (!pendingId) {
        log("fetchCloseRequestForApplication:none", { appId });
        return { applicationId: appId, pending: null, fromCache: false };
      }

      const detail = await retrieveCloseRequest(pendingId);

      if (!isPending(detail)) {
        log("fetchCloseRequestForApplication:pendingId_not_pending", {
          appId,
          id: detail?.id,
          status: detail?.status,
        });
        return { applicationId: appId, pending: null, fromCache: false };
      }

      log("fetchCloseRequestForApplication:ok", {
        appId,
        id: detail?.id,
        status: detail?.status,
      });

      return { applicationId: appId, pending: detail, fromCache: false };
    } catch (err) {
      warn("fetchCloseRequestForApplication:err", err);
      return rejectWithValue(normalizeError(err, "Kapatma talebi alınamadı."));
    }
  }
);

/**
 *   META: Sonuç kartında kullanılacak "en güncel approved" talep detayı.
 * Bu thunk HISTORY'yi kullanmaz. Böylece:
 * - Sonuç kartı meta'sı "Geçmişi Göster" ile değişmez.
 *
 * Param:
 *  fetchLatestApprovedCloseRequestMetaForApplication({ applicationId, force=false })
 */
export const fetchLatestApprovedCloseRequestMetaForApplication = createAsyncThunk(
  "belediyeCloseApproval/fetchLatestApprovedCloseRequestMetaForApplication",
  async ({ applicationId, force = false } = {}, { getState, rejectWithValue }) => {
    try {
      const appId = Number(applicationId);
      if (!appId) throw new Error("applicationId zorunlu");

      const state = getState?.();
      const mb = state?.belediyeCloseApproval?.metaByApplicationId?.[String(appId)];
      const cached = mb?.latestApproved || null;
      const hasCache = Boolean(cached?.id) && Boolean(mb?.lastFetchedAt);

      if (!force && hasCache) {
        log("fetchLatestApprovedMeta:cache-hit", { appId, id: cached?.id });
        return { applicationId: appId, latestApproved: cached, fromCache: true };
      }

      log("fetchLatestApprovedMeta:start", { appId, force });

      const list = await listCloseRequests({});
      const approvedId = pickLatestApprovedIdForApplication(list, appId);

      if (!approvedId) {
        log("fetchLatestApprovedMeta:none", { appId });
        return { applicationId: appId, latestApproved: null, fromCache: false };
      }

      const detail = await retrieveCloseRequest(approvedId);

      if (!isApproved(detail)) {
        // defans: id approved değilse meta’yı boş geç
        log("fetchLatestApprovedMeta:not-approved", {
          appId,
          id: detail?.id,
          status: detail?.status,
        });
        return { applicationId: appId, latestApproved: null, fromCache: false };
      }

      log("fetchLatestApprovedMeta:ok", { appId, id: detail?.id });

      return { applicationId: appId, latestApproved: detail, fromCache: false };
    } catch (err) {
      warn("fetchLatestApprovedMeta:err", err);
      return rejectWithValue(normalizeError(err, "Talep meta alınamadı."));
    }
  }
);

/**
 *   Geçmiş talepler (pending olmayanlar) — LAZY LOAD için.
 *
 * - Cache varsa (force değilse) tekrar fetch etmez.
 * - listCloseRequests() -> client filter
 * - top N id -> retrieve detail
 *
 * Param:
 *  fetchCloseRequestsHistoryForApplication({ applicationId, maxItems=10, force=false })
 */
export const fetchCloseRequestsHistoryForApplication = createAsyncThunk(
  "belediyeCloseApproval/fetchCloseRequestsHistoryForApplication",
  async (
    { applicationId, maxItems = 10, force = false } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const appId = Number(applicationId);
      if (!appId) throw new Error("applicationId zorunlu");

      const state = getState?.();
      const hb = state?.belediyeCloseApproval?.historyByApplicationId?.[String(appId)];
      const cachedItems = Array.isArray(hb?.items) ? hb.items : [];
      const hasCache = cachedItems.length > 0 && Boolean(hb?.lastFetchedAt);

      if (!force && hasCache) {
        log("fetchCloseRequestsHistoryForApplication:cache-hit", {
          appId,
          count: cachedItems.length,
        });
        return { applicationId: appId, items: cachedItems, fromCache: true };
      }

      const limit = Math.max(1, Math.min(Number(maxItems) || 10, 25));
      log("fetchCloseRequestsHistoryForApplication:start", { appId, limit, force });

      const list = await listCloseRequests({});
      const finals = pickFinalItemsForApplication(list, appId).slice(0, limit);

      const details = await Promise.all(
        finals.map((x) => retrieveCloseRequest(x.id).catch(() => null))
      );

      const cleaned = uniqByIdDesc(details.filter(Boolean));
      log("fetchCloseRequestsHistoryForApplication:ok", { appId, count: cleaned.length });

      return { applicationId: appId, items: cleaned, fromCache: false };
    } catch (err) {
      warn("fetchCloseRequestsHistoryForApplication:err", err);
      return rejectWithValue(normalizeError(err, "Geçmiş talepler alınamadı."));
    }
  }
);

/**
 * Personel: talep oluştur (pending).
 */
export const createCloseRequest = createAsyncThunk(
  "belediyeCloseApproval/createCloseRequest",
  async (
    { application_id, proposed_status, proposed_note = "", internal_note = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const appId = Number(application_id);
      if (!appId) throw new Error("application_id zorunlu");

      const payload = {
        application_id: appId,
        proposed_status,
        proposed_note,
        internal_note,
      };

      log("createCloseRequest:start", payload);

      const detail = await apiCreateCloseRequest(payload);

      log("createCloseRequest:ok", { id: detail?.id, status: detail?.status });

      return { applicationId: appId, closeRequest: detail };
    } catch (err) {
      warn("createCloseRequest:err", err);
      return rejectWithValue(normalizeError(err, "Kapatma talebi oluşturulamadı."));
    }
  }
);

/**
 * Talep iptal (pending -> cancelled).
 */
export const cancelCloseRequest = createAsyncThunk(
  "belediyeCloseApproval/cancelCloseRequest",
  async (
    { id, applicationId, note = "", internal_note = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const crId = Number(id);
      if (!crId) throw new Error("closeRequest id zorunlu");

      const appId = Number(applicationId);
      if (!appId) throw new Error("applicationId zorunlu");

      log("cancelCloseRequest:start", { crId, appId });

      const detail = await apiCancelCloseRequest(crId, { note, internal_note });

      log("cancelCloseRequest:ok", { id: detail?.id, status: detail?.status, appId });

      return { applicationId: appId, closeRequest: detail };
    } catch (err) {
      warn("cancelCloseRequest:err", err);
      return rejectWithValue(normalizeError(err, "Talep iptal edilemedi."));
    }
  }
);

/**
 * Müdür/Başkan: approve/reject
 */
export const decideCloseRequest = createAsyncThunk(
  "belediyeCloseApproval/decideCloseRequest",
  async (
    {
      id,
      applicationId,
      decision,
      final_status,
      final_note,
      decision_note = "",
      internal_note = "",
      refreshApplication = true,
    } = {},
    { dispatch, rejectWithValue }
  ) => {
    try {
      const crId = Number(id);
      if (!crId) throw new Error("closeRequest id zorunlu");

      const appId = Number(applicationId);
      if (!appId) throw new Error("applicationId zorunlu");

      log("decideCloseRequest:start", {
        crId,
        appId,
        decision,
        final_status,
        hasFinalNote: final_note !== undefined && String(final_note || "").length > 0,
      });

      const detail = await apiDecideCloseRequest(crId, {
        decision,
        ...(final_status ? { final_status } : {}),
        ...(final_note !== undefined ? { final_note } : {}),
        ...(decision_note !== undefined ? { decision_note } : {}),
        ...(internal_note !== undefined ? { internal_note } : {}),
      });

      log("decideCloseRequest:ok", { id: detail?.id, status: detail?.status, appId });

      if (refreshApplication && appId) {
        const token = getAccessTokenSafe();
        if (token) {
          dispatch(fetchApplicationDetails({ id: appId, token }));
          dispatch(fetchApplicationStatusLogs({ applicationId: appId, token }));
        }
      }

      return { applicationId: appId, closeRequest: detail };
    } catch (err) {
      warn("decideCloseRequest:err", err);
      return rejectWithValue(normalizeError(err, "Karar işlemi başarısız."));
    }
  }
);

// -------------------------------------------------------------
// Slice
// -------------------------------------------------------------
const initialState = {
  byApplicationId: {},
  historyByApplicationId: {},

  //   sonuç kartı için meta cache (history’den bağımsız)
  metaByApplicationId: {
    // [appId]: { latestApproved: CloseRequestDetail|null, loading, error, lastFetchedAt }
  },

  creating: false,
  cancelling: false,
  deciding: false,

  lastError: null,
};

const ensureAppBucket = (state, appId) => {
  const key = String(appId);
  if (!state.byApplicationId[key]) {
    state.byApplicationId[key] = {
      current: null,
      loading: false,
      error: null,
      lastFetchedAt: null,
    };
  }
  return state.byApplicationId[key];
};

const ensureHistoryBucket = (state, appId) => {
  const key = String(appId);
  if (!state.historyByApplicationId[key]) {
    state.historyByApplicationId[key] = {
      items: [],
      loading: false,
      error: null,
      lastFetchedAt: null,
    };
  }
  return state.historyByApplicationId[key];
};

//   meta bucket
const ensureMetaBucket = (state, appId) => {
  const key = String(appId);
  if (!state.metaByApplicationId[key]) {
    state.metaByApplicationId[key] = {
      latestApproved: null,
      loading: false,
      error: null,
      lastFetchedAt: null,
    };
  }
  return state.metaByApplicationId[key];
};

const pushToHistory = (state, appId, closeRequest) => {
  if (!appId || !closeRequest?.id) return;
  if (!isFinal(closeRequest)) return;

  const hb = ensureHistoryBucket(state, appId);
  hb.items = uniqByIdDesc([closeRequest, ...(hb.items || [])]);
  hb.lastFetchedAt = new Date().toISOString();
};

const clearCurrentPending = (state, appId) => {
  const bucket = ensureAppBucket(state, appId);
  bucket.current = null;
  bucket.loading = false;
  bucket.error = null;
  bucket.lastFetchedAt = new Date().toISOString();
};

const setCurrentPending = (state, appId, closeRequest) => {
  const bucket = ensureAppBucket(state, appId);
  bucket.current = isPending(closeRequest) ? closeRequest : null;
  bucket.loading = false;
  bucket.error = null;
  bucket.lastFetchedAt = new Date().toISOString();
};

const setLatestApprovedMeta = (state, appId, closeRequestOrNull) => {
  const mb = ensureMetaBucket(state, appId);
  mb.latestApproved = closeRequestOrNull && isApproved(closeRequestOrNull) ? closeRequestOrNull : null;
  mb.loading = false;
  mb.error = null;
  mb.lastFetchedAt = new Date().toISOString();
};

const markMetaStale = (state, appId) => {
  const mb = ensureMetaBucket(state, appId);
  mb.lastFetchedAt = null;
};

const belediyeCloseApprovalSlice = createSlice({
  name: "belediyeCloseApproval",
  initialState,
  reducers: {
    clearCloseApprovalForApplication(state, action) {
      const appId = Number(action.payload);
      if (!appId) return;
      delete state.byApplicationId[String(appId)];
      delete state.historyByApplicationId[String(appId)];
      delete state.metaByApplicationId[String(appId)];
    },
    clearAllCloseApproval(state) {
      state.byApplicationId = {};
      state.historyByApplicationId = {};
      state.metaByApplicationId = {};
      state.creating = false;
      state.cancelling = false;
      state.deciding = false;
      state.lastError = null;
    },

    // UI manual set
    setCloseRequestForApplication(state, action) {
      const { applicationId, closeRequest } = action.payload || {};
      const appId = Number(applicationId);
      if (!appId) return;

      setCurrentPending(state, appId, closeRequest || null);

      if (isFinal(closeRequest)) {
        pushToHistory(state, appId, closeRequest);
      }
    },

    setCloseRequestHistoryForApplication(state, action) {
      const { applicationId, items } = action.payload || {};
      const appId = Number(applicationId);
      if (!appId) return;
      const hb = ensureHistoryBucket(state, appId);
      hb.items = uniqByIdDesc(items || []);
      hb.loading = false;
      hb.error = null;
      hb.lastFetchedAt = new Date().toISOString();
    },

    //   Lazy-load butonu için: history cache stale
    markCloseRequestHistoryStale(state, action) {
      const appId = Number(action.payload);
      if (!appId) return;
      const hb = ensureHistoryBucket(state, appId);
      hb.lastFetchedAt = null;
    },

    //   meta cache stale (sonuç kartı meta’sını tekrar çekmek istersen)
    markCloseRequestMetaStale(state, action) {
      const appId = Number(action.payload);
      if (!appId) return;
      markMetaStale(state, appId);
    },
  },
  extraReducers: (builder) => {
    // fetchCloseRequestForApplication
    builder
      .addCase(fetchCloseRequestForApplication.pending, (state, action) => {
        const appId = Number(action.meta?.arg?.applicationId);
        if (!appId) return;
        const bucket = ensureAppBucket(state, appId);
        bucket.loading = true;
        bucket.error = null;
      })
      .addCase(fetchCloseRequestForApplication.fulfilled, (state, action) => {
        const { applicationId, pending } = action.payload || {};
        const appId = Number(applicationId);
        if (!appId) return;
        setCurrentPending(state, appId, pending || null);
      })
      .addCase(fetchCloseRequestForApplication.rejected, (state, action) => {
        const appId = Number(action.meta?.arg?.applicationId);
        if (!appId) return;
        const bucket = ensureAppBucket(state, appId);
        bucket.loading = false;
        bucket.error =
          action.payload || action.error?.message || "Kapatma talebi alınamadı.";
        state.lastError = bucket.error;
      });

    //   fetchLatestApprovedCloseRequestMetaForApplication
    builder
      .addCase(fetchLatestApprovedCloseRequestMetaForApplication.pending, (state, action) => {
        const appId = Number(action.meta?.arg?.applicationId);
        if (!appId) return;
        const mb = ensureMetaBucket(state, appId);
        mb.loading = true;
        mb.error = null;
      })
      .addCase(fetchLatestApprovedCloseRequestMetaForApplication.fulfilled, (state, action) => {
        const { applicationId, latestApproved } = action.payload || {};
        const appId = Number(applicationId);
        if (!appId) return;
        setLatestApprovedMeta(state, appId, latestApproved || null);
      })
      .addCase(fetchLatestApprovedCloseRequestMetaForApplication.rejected, (state, action) => {
        const appId = Number(action.meta?.arg?.applicationId);
        if (!appId) return;
        const mb = ensureMetaBucket(state, appId);
        mb.loading = false;
        mb.error = action.payload || action.error?.message || "Talep meta alınamadı.";
        state.lastError = mb.error;
      });

    // fetchCloseRequestsHistoryForApplication
    builder
      .addCase(fetchCloseRequestsHistoryForApplication.pending, (state, action) => {
        const appId = Number(action.meta?.arg?.applicationId);
        if (!appId) return;
        const hb = ensureHistoryBucket(state, appId);
        hb.loading = true;
        hb.error = null;
      })
      .addCase(fetchCloseRequestsHistoryForApplication.fulfilled, (state, action) => {
        const { applicationId, items } = action.payload || {};
        const appId = Number(applicationId);
        if (!appId) return;
        const hb = ensureHistoryBucket(state, appId);
        hb.items = uniqByIdDesc(items || []);
        hb.loading = false;
        hb.error = null;
        hb.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchCloseRequestsHistoryForApplication.rejected, (state, action) => {
        const appId = Number(action.meta?.arg?.applicationId);
        if (!appId) return;
        const hb = ensureHistoryBucket(state, appId);
        hb.loading = false;
        hb.error =
          action.payload || action.error?.message || "Geçmiş talepler alınamadı.";
        state.lastError = hb.error;
      });

    // createCloseRequest
    builder
      .addCase(createCloseRequest.pending, (state) => {
        state.creating = true;
        state.lastError = null;
      })
      .addCase(createCloseRequest.fulfilled, (state, action) => {
        state.creating = false;
        const { applicationId, closeRequest } = action.payload || {};
        const appId = Number(applicationId);
        if (!appId) return;

        setCurrentPending(state, appId, closeRequest || null);

        // history stale
        const hb = ensureHistoryBucket(state, appId);
        hb.lastFetchedAt = null;

        // meta stale (talep oluşturulunca approved meta değişmez ama güvenli)
        markMetaStale(state, appId);
      })
      .addCase(createCloseRequest.rejected, (state, action) => {
        state.creating = false;
        state.lastError =
          action.payload || action.error?.message || "Talep oluşturulamadı.";
      });

    // cancelCloseRequest
    builder
      .addCase(cancelCloseRequest.pending, (state) => {
        state.cancelling = true;
        state.lastError = null;
      })
      .addCase(cancelCloseRequest.fulfilled, (state, action) => {
        state.cancelling = false;
        const { applicationId, closeRequest } = action.payload || {};
        const appId = Number(applicationId);
        if (!appId) return;

        pushToHistory(state, appId, closeRequest);
        clearCurrentPending(state, appId);

        // meta stale (iptal -> approved meta etkilenmez ama güvenli)
        markMetaStale(state, appId);
      })
      .addCase(cancelCloseRequest.rejected, (state, action) => {
        state.cancelling = false;
        state.lastError =
          action.payload || action.error?.message || "Talep iptal edilemedi.";
      });

    // decideCloseRequest
    builder
      .addCase(decideCloseRequest.pending, (state) => {
        state.deciding = true;
        state.lastError = null;
      })
      .addCase(decideCloseRequest.fulfilled, (state, action) => {
        state.deciding = false;
        const { applicationId, closeRequest } = action.payload || {};
        const appId = Number(applicationId);
        if (!appId) return;

        pushToHistory(state, appId, closeRequest);
        clearCurrentPending(state, appId);

        //   approved karar geldiyse meta’yı direkt güncelle (anlık ve hızlı)
        if (isApproved(closeRequest)) {
          setLatestApprovedMeta(state, appId, closeRequest);
        } else {
          // reject/cancel -> meta stale
          markMetaStale(state, appId);
        }
      })
      .addCase(decideCloseRequest.rejected, (state, action) => {
        state.deciding = false;
        state.lastError =
          action.payload || action.error?.message || "Karar verilemedi.";
      });
  },
});

export const {
  clearCloseApprovalForApplication,
  clearAllCloseApproval,
  setCloseRequestForApplication,
  setCloseRequestHistoryForApplication,
  markCloseRequestHistoryStale,
  markCloseRequestMetaStale,
} = belediyeCloseApprovalSlice.actions;

export default belediyeCloseApprovalSlice.reducer;

// -------------------------------------------------------------
// Selectors
// -------------------------------------------------------------
export const selectCloseApprovalState = (state) => state?.belediyeCloseApproval;

export const selectCloseApprovalBucket = (state, applicationId) => {
  const s = selectCloseApprovalState(state);
  const appId = Number(applicationId);
  if (!s || !appId) return null;
  return s.byApplicationId?.[String(appId)] || null;
};

export const selectCloseRequestForApplication = (state, applicationId) =>
  selectCloseApprovalBucket(state, applicationId)?.current || null;

export const selectHasPendingCloseRequestForApplication = (state, applicationId) =>
  Boolean(selectCloseRequestForApplication(state, applicationId)?.id);

export const selectCloseApprovalLoadingForApplication = (state, applicationId) =>
  Boolean(selectCloseApprovalBucket(state, applicationId)?.loading);

export const selectCloseApprovalErrorForApplication = (state, applicationId) =>
  selectCloseApprovalBucket(state, applicationId)?.error || null;

export const selectCloseApprovalOps = (state) => {
  const s = selectCloseApprovalState(state) || {};
  return {
    creating: Boolean(s.creating),
    cancelling: Boolean(s.cancelling),
    deciding: Boolean(s.deciding),
    lastError: s.lastError || null,
  };
};

// history selectors
export const selectCloseApprovalHistoryBucket = (state, applicationId) => {
  const s = selectCloseApprovalState(state);
  const appId = Number(applicationId);
  if (!s || !appId) return null;
  return s.historyByApplicationId?.[String(appId)] || null;
};

export const selectCloseRequestsHistoryForApplication = (state, applicationId) =>
  selectCloseApprovalHistoryBucket(state, applicationId)?.items || [];

export const selectCloseRequestsHistoryLoadingForApplication = (state, applicationId) =>
  Boolean(selectCloseApprovalHistoryBucket(state, applicationId)?.loading);

export const selectCloseRequestsHistoryErrorForApplication = (state, applicationId) =>
  selectCloseApprovalHistoryBucket(state, applicationId)?.error || null;

export const selectCloseRequestsHistoryLoadedForApplication = (state, applicationId) => {
  const hb = selectCloseApprovalHistoryBucket(state, applicationId);
  return Boolean(hb?.lastFetchedAt) && Array.isArray(hb?.items) && hb.items.length > 0;
};

//   Sonuç kartı için: en güncel “final” talep (approved/rejected/cancelled)
// (History fetch edilmişse anlamlıdır; değilse null olabilir.)
export const selectLatestFinalCloseRequestForApplication = (state, applicationId) => {
  const items = selectCloseRequestsHistoryForApplication(state, applicationId);
  return Array.isArray(items) && items.length ? items[0] : null;
};

// -------------------------------------------------------------
//   META selectors (history’den bağımsız)
// -------------------------------------------------------------
export const selectCloseApprovalMetaBucket = (state, applicationId) => {
  const s = selectCloseApprovalState(state);
  const appId = Number(applicationId);
  if (!s || !appId) return null;
  return s.metaByApplicationId?.[String(appId)] || null;
};

export const selectLatestApprovedMetaForApplication = (state, applicationId) =>
  selectCloseApprovalMetaBucket(state, applicationId)?.latestApproved || null;

export const selectLatestApprovedMetaLoadingForApplication = (state, applicationId) =>
  Boolean(selectCloseApprovalMetaBucket(state, applicationId)?.loading);

export const selectLatestApprovedMetaErrorForApplication = (state, applicationId) =>
  selectCloseApprovalMetaBucket(state, applicationId)?.error || null;

export const selectLatestApprovedMetaLoadedForApplication = (state, applicationId) => {
  const mb = selectCloseApprovalMetaBucket(state, applicationId);
  return Boolean(mb?.lastFetchedAt);
};

// Eski selector (history’ye bağlı) — bug üretmesin diye DEPRECATE et.
// FinalStatusStep sonuç kartında bunu KULLANMAYACAĞIZ.
export const selectLatestApprovedCloseRequestForApplication = (state, applicationId) => {
  return selectLatestApprovedMetaForApplication(state, applicationId);
};